import { useState } from 'react';
import { useSetRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { Box, Button, Grid, GridItem } from '@flos/react-ui';
import { useHippoText } from '../../hooks/useHippoText';
import { getCoveragesWithProps } from './config/AdditionalCoveragesConfig';
import { getOnsaCoverable } from '../../utils/coverablesUtil';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { shoppingBasketOverlayAtom } from '../../recoil/atoms/shoppingBasketOverlayAtom';
import { hippoCoverageTextsSelector } from '../../recoil/selectors/hippoCoverageTextsSelector';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import WizardPagetemplate from '../../templates/WizardPagetemplate';
import AdditionalCoverage from './components/AdditionalCoverage';
import TotalPrice from '../../shared_components/TotalPrice/TotalPrice';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import _ from 'lodash';
import { trackAddProduct } from '../../utils/trackingUtil';
import { additionalCoveragesSelector } from '../../recoil/selectors/additionalCoveragesSelector';
import { isSavingPackageAtom } from '../../recoil/atoms/isSavingPackageAtom';
import { currentPackageGroupAtom } from '../../recoil/atoms/currentPackageGroupAtom';
import type { CoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';

const AdditionalCoveragePage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const [isLoading, setIsLoading] = useState(false);
    const getHippoText = useHippoText();
    const setShoppingBasketOverlayInfo = useSetRecoilState(shoppingBasketOverlayAtom);
    const setIsSavingPackage = useSetRecoilState(isSavingPackageAtom);
    const resetCurrentPackageGroup = useResetRecoilState(currentPackageGroupAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName = '', fixedId = '' } = currentProduct ?? {};
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable =
        flavourName && fixedId ? getOnsaCoverable(coverables, flavourName, fixedId) : undefined;
    const coverages = useRecoilValue<CoverageDTO[]>(additionalCoveragesSelector);
    const onsaCoverageTexts = useRecoilValue(
        hippoCoverageTextsSelector({
            coverageIds: coverages.map((cov) => cov.coverageId),
            termIds: coverable?.termIds ?? []
        })
    );
    const additionalCoverages = getCoveragesWithProps(coverages, flavourName, onsaCoverageTexts);

    const goBack = (): void => {
        setIsLoading(true);
        wizardGotoStep('CoveragePackagePage');
    };

    const beforeNext = async (): Promise<void> => {
        setIsSavingPackage(true);
        resetCurrentPackageGroup();
        await Promise.resolve();
    };

    const noSelectedCoverage = (): boolean => {
        const uniqueCoveragesLength = _.uniq(
            (coverable?.coverages?.map((cov) => cov.coverageId) ?? []).concat(
                coverages.map((cov) => cov.coverageId)
            )
        ).length;
        const coveragesLength = (coverable?.coverages?.length ?? 0) + coverages.length;
        return coveragesLength === uniqueCoveragesLength;
    };

    if (!currentProduct || !coverable) {
        return <></>;
    }

    return (
        <WizardPagetemplate hidePrevious hideNext>
            <Grid gap={7}>
                <GridItem span={8}>
                    <h3 className="u-spacing-stack-none">
                        {getHippoText('DSS-ADDITIONAL-COVERAGE-PAGE-HEADER')}
                    </h3>
                </GridItem>
                <GridItem
                    className="u-flex-box u-flex-box--col u-spacing-stack-l"
                    colStart={1}
                    span={8}
                >
                    {additionalCoverages.map((coverage) => (
                        <AdditionalCoverage
                            key={coverage.coverageName}
                            coverage={coverage}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                        />
                    ))}
                </GridItem>
                <GridItem className="u-spacing-stack-l" colStart={9} span={4}>
                    <Box theme="secondary">
                        <TotalPrice
                            totalPrice={coverable.monthlyPrice?.amount ?? 0}
                            showYearlyPriceField={true}
                            paymentFrequencyPostfix="/md"
                            overrideLabel={getHippoText('DSS-ADDITIONAL-COVERAGE-TOTAL-PRICE')}
                        />
                    </Box>
                </GridItem>
            </Grid>
            <Button
                onClick={() => {
                    trackAddProduct(coverable);
                    setShoppingBasketOverlayInfo({
                        isButtonDisplayed: true,
                        isOverlayOpen: true,
                        beforeGoNext: beforeNext
                    });
                }}
                disabled={isLoading}
            >
                {noSelectedCoverage()
                    ? getHippoText('DSS-ADDITIONAL-COVERAGE-NOTHANKYOU')
                    : getHippoText('DSS-ADDITIONAL-COVERAGE-CONTINUE')}
            </Button>
            <Button onClick={goBack} theme="ghost" disabled={isLoading}>
                {getHippoText('DSS-COMMON-BACK')}
            </Button>
        </WizardPagetemplate>
    );
};

export default AdditionalCoveragePage;
